import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { BetDock } from './BetDock'

const QUICK = [10, 50, 100, 500]

type Props = {
  balance: number
  oddsHigher: number
  oddsLower: number
  busy: boolean
  hasDeal: boolean
  onDeal: () => void
  onPlay: (amount: number, choice: 'higher' | 'lower') => void
  pendingChoice: 'higher' | 'lower' | null
  balanceStale: boolean
  balanceRefreshing: boolean
  onRetryBalance: () => void
  error?: string | null
}

export function BetPanel({
  balance,
  oddsHigher,
  oddsLower,
  busy,
  hasDeal,
  onDeal,
  onPlay,
  pendingChoice,
  balanceStale,
  balanceRefreshing,
  onRetryBalance,
  error,
}: Props) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState(100)

  // Tier 1: Odds segment
  const parameterSlot = hasDeal ? (
    <div className="bet-dock-segmented">
      <div
        className="bet-dock-segment-btn active"
        style={{ color: '#fff', cursor: 'default' }}
      >
        ▲ {t('higher')} {oddsHigher > 0 ? `${oddsHigher.toFixed(2)}x` : t('na')}
      </div>
      <div
        className="bet-dock-segment-btn active"
        style={{ color: '#fff', cursor: 'default' }}
      >
        ▼ {t('lower')} {oddsLower > 0 ? `${oddsLower.toFixed(2)}x` : t('na')}
      </div>
    </div>
  ) : null

  const [showChips, setShowChips] = useState(false)

  // Tier 2: Stake input & Collapsible Quick chips
  const amountSlot = (
    <div className="bet-dock-tier-amount">
      <div className="bet-dock-amount-main-row">
        <div className="bet-dock-input-group">
          <label htmlFor="hilo-amt">{t('betAmount')}</label>
          <input
            id="hilo-amt"
            type="number"
            min={10}
            max={balance}
            value={amount}
            disabled={busy || balanceRefreshing}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
        <div className="bet-dock-quick-multipliers">
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy || balanceRefreshing}
            onClick={() => setAmount(Math.max(10, Math.floor(amount / 2)))}
          >
            1/2
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy || balanceRefreshing}
            onClick={() => setAmount(Math.max(10, Math.min(balance, amount * 2)))}
          >
            2X
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy || balanceRefreshing}
            onClick={() => setAmount(Math.max(10, balance))}
          >
            {t('max')}
          </button>
          <button
            type="button"
            className={`bet-dock-chip-toggle${showChips ? ' active' : ''}`}
            disabled={busy || balanceRefreshing}
            onClick={() => setShowChips(!showChips)}
            title={t('quickChips')}
            aria-label={t('quickChips')}
            aria-expanded={showChips}
            aria-controls="hilo-quick-chips"
          >
            {showChips ? '▲' : '⚡'}
          </button>
        </div>
      </div>
      {showChips && (
        <div className="bet-dock-collapsible-chips" id="hilo-quick-chips">
          {QUICK.map((q) => (
            <button
              key={q}
              type="button"
              className="bet-dock-preset-chip"
              disabled={busy || balanceRefreshing}
              onClick={() => {
                setAmount(q)
                setShowChips(false)
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // Tier 3: Primary Action CTA
  const ctaSlot = balanceStale ? (
    <button
      type="button"
      className="bet-dock-cta-btn state-ready"
      disabled={busy || balanceRefreshing}
      onClick={onRetryBalance}
    >
      {balanceRefreshing ? t('refreshingBalance') : t('retryBalance')}
    </button>
  ) : !hasDeal ? (
    <motion.button
      type="button"
      className="bet-dock-cta-btn state-ready"
      whileTap={{ scale: 0.98 }}
      disabled={busy}
      onClick={onDeal}
    >
      {t('deal')}
    </motion.button>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: '100%' }}>
      <motion.button
        type="button"
        className={`bet-dock-cta-btn state-ready${pendingChoice === 'higher' ? ' pulse' : ''}`}
        whileTap={{ scale: 0.97 }}
        disabled={busy || oddsHigher <= 0 || amount < 10 || amount > balance}
        aria-pressed={pendingChoice === 'higher'}
        onClick={() => onPlay(amount, 'higher')}
      >
        ▲ {t('higher')} ({oddsHigher > 0 ? `${oddsHigher.toFixed(2)}x` : t('na')})
      </motion.button>
      <motion.button
        type="button"
        className={`bet-dock-cta-btn state-ready${pendingChoice === 'lower' ? ' pulse' : ''}`}
        whileTap={{ scale: 0.97 }}
        disabled={busy || oddsLower <= 0 || amount < 10 || amount > balance}
        aria-pressed={pendingChoice === 'lower'}
        onClick={() => onPlay(amount, 'lower')}
      >
        ▼ {t('lower')} ({oddsLower > 0 ? `${oddsLower.toFixed(2)}x` : t('na')})
      </motion.button>
    </div>
  )

  const metaSlot = (
    <>
      <span>{t('balance', { amount: Math.round(balance) })}</span>
      <span>{t('betAmount')}: {amount}</span>
    </>
  )

  return (
    <BetDock
      className="hilo-bet-dock"
      parameterSlot={parameterSlot}
      amountSlot={amountSlot}
      ctaSlot={ctaSlot}
      metaSlot={metaSlot}
      error={error}
    />
  )
}
